import { action, computed, makeObservable, reaction, runInAction } from 'mobx';

import { apiUrls } from 'shared/entities/domain';
import { IProjectTagsStore } from 'shared/entities/store/projectTagsStore';
import { IRootStore } from 'shared/entities/store/rootStore';
import { AppNotificationType } from 'shared/entities/appNotifications';
import {
  mapProjectTagErrorCodeToMessage,
  ProjectTagErrorCode,
  ProjectTagServer
} from 'shared/entities/tags';
import NewListModel from 'shared/models/NewListModel';
import { ComponentLoadingStore } from 'stores/componentLoadingStore';
import ProjectTagModel from 'shared/models/tags/ProjectTagModel';
import { ValidatorResult } from 'shared/entities/validator';
import { LoadingStage } from 'shared/entities/meta';

export default class ProjectTagsStore
  extends ComponentLoadingStore
  implements IProjectTagsStore
{
  readonly tags: NewListModel<ProjectTagModel> =
    new NewListModel<ProjectTagModel>({
      keys: [],
      entities: new Map(),
      disabledLoadMore: true
    });
  readonly rootStore: IRootStore;

  constructor(rootStore: IRootStore) {
    super();

    this.rootStore = rootStore;

    makeObservable(this, {
      validateNameAvailable: action,
      silentLoadingStage: computed
    });
  }

  get loaded(): boolean {
    return this.tags.loadingStage.successfullyLoaded;
  }

  get canBeLoaded(): boolean {
    return this.rootStore.initialized && this.rootStore.userStore.isAuthorized;
  }

  get silentLoadingStage(): LoadingStage {
    return !this.tags.isInitialLoad && this.tags.loadingStage.isLoading
      ? LoadingStage.SUCCESS
      : this.tags.loadingStage.value;
  }

  validateNameAvailable(name: string, excludeId?: string): ValidatorResult {
    const trimmed = name.trim();

    if (!trimmed) {
      return null;
    }

    if (excludeId) {
      const tag = this.tags.getEntity(excludeId);

      if (tag && trimmed === tag.name.trim()) {
        return null;
      }
    }

    if (ProjectTagModel.isDuplicateName(this.tags.items, name, excludeId)) {
      return mapProjectTagErrorCodeToMessage(
        ProjectTagErrorCode.tagAlreadyExists
      );
    }

    return null;
  }
  initReactions(): void {
    super.initReactions();

    this.addReaction({
      key: 'projectId',
      reaction: reaction(
        () => this.rootStore.projectId,
        () => {
          this.reset();
        }
      )
    });
  }

  async load(): Promise<BaseResponse> {
    if (!this.canBeLoaded || this.tags.loadingStage.isLoading) {
      return { isError: true };
    }

    this.tags.loadingStage.loading();

    const response = await this.rootStore.networkStore.api<{
      tags: ProjectTagServer[];
    }>(apiUrls.USER_TAGS_LIST, { method: 'GET' });

    runInAction(() => {
      if (response.isError) {
        this.tags.loadingStage.error();

        return;
      }

      this.tags.fillByRawData({
        raw: response.data.tags,
        normalizer: (raw) => ProjectTagModel.toListItem(raw, this.rootStore),
        initial: true
      });
      this.tags.isInitialLoadModel.changeValue(false);

      this.tags.loadingStage.success();
    });

    return { isError: response.isError };
  }

  async create(
    name: string
  ): Promise<
    BaseResponse<ProjectTagModel, { errorCode: ProjectTagErrorCode | null }>
  > {
    const trimmed = name.trim();
    const isError = ProjectTagModel.validateName(trimmed);
    if (isError) {
      return { isError: true, data: { errorCode: null } };
    }

    if (this.tags.creatingStage.isLoading) {
      return {
        isError: true,
        data: { errorCode: null }
      };
    }

    this.tags.creatingStage.loading();

    const response = await this.rootStore.networkStore.api<{
      tag: ProjectTagServer;
    }>(apiUrls.USER_TAGS_CREATE, {
      method: 'POST',
      data: { name: trimmed },
      errorsMap: mapProjectTagErrorCodeToMessage,
      showUnexpectedError: false
    });

    if (response.isError) {
      const errorCode = response.data?.code as ProjectTagErrorCode | undefined;

      runInAction(() => {
        this.tags.creatingStage.error();
      });

      if (
        errorCode !== ProjectTagErrorCode.tagAlreadyExists &&
        errorCode !== ProjectTagErrorCode.tagNotFound
      ) {
        this.rootStore.appNotificationsStore.open({
          type: AppNotificationType.error,
          title: (t) => t('ProjectTagsStore.createError', { ns: 'stores' })
        });
      }

      return {
        isError: true,
        data: { errorCode: errorCode ?? null }
      };
    }

    const tag = ProjectTagModel.fromJson(response.data.tag, this.rootStore);

    runInAction(() => {
      if (this.tags.getEntity(tag.id)) {
        this.tags.updateEntity(tag.id, tag);
      } else {
        this.tags.addEntity({ entity: tag, key: tag.id });
      }
      this.tags.creatingStage.success();
    });

    this.rootStore.appNotificationsStore.open({
      type: AppNotificationType.success,
      title: (t) =>
        t('ProjectTagsStore.createSuccess', { ns: 'stores', name: tag.name }),
      highlightedParts: tag.name ? [tag.name] : undefined
    });

    return {
      isError: false,
      data: tag
    };
  }

  async deleteTag(tag: ProjectTagModel): Promise<BaseResponse> {
    const { isError } = await tag.delete();

    if (!isError) {
      this.tags.removeEntity(tag.id);
    }

    return { isError };
  }

  reset(): void {
    this.tags.reset();
  }
}
